import { useMemo } from 'react';
import { useAppStore, useInvestmentData, useTeamData, CURRENCY_RATES, type Currency, type InvestmentFormData } from '@/stores/appStore';

export function useProjectMetrics(formValuesOverride?: Partial<InvestmentFormData>) {
  const state = useAppStore();
  const { data: investmentData } = useInvestmentData();
  const { teamRoles } = useTeamData();
  const { currency, planningMonths } = state;

  // Use formValuesOverride if provided (from form watch), otherwise use investmentData from store
  const formValues = formValuesOverride || investmentData;

  // Calculate projection data
  const projectionData = useMemo(() => {
    if (!formValues) return [];

    const data = [];
    const months = planningMonths;

    for (let month = 0; month < months; month++) {
      const users = Math.floor((formValues.initialUsers || 0) * Math.pow(1 + (formValues.monthlyGrowthRate || 0) / 100, month));

      // Revenue calculations
      const soloRevenue = users * 0.3 * (formValues.soloPrice || 0) * (1 + (formValues.soloGrowthRate || 0) / 100);
      const teamRevenue = users * 0.5 * (formValues.teamPrice || 0) * (1 + (formValues.teamGrowthRate || 0) / 100);
      const unlimitedRevenue = users * 0.2 * (formValues.unlimitedPrice || 0) * (1 + (formValues.unlimitedGrowthRate || 0) / 100);
      const totalRevenue = soloRevenue + teamRevenue + unlimitedRevenue;

      // Calculate team salaries for this month
      const teamSalaries = teamRoles.reduce((total, role) => {
        const headcount = role.headcount[month] || 0;
        return total + (role.salary * headcount);
      }, 0);

      // Expense calculations
      let monthlyExpenses = formValues.monthlyOpex || 0;
      if (formValues.enableExpenseGrowth) {
        const growthFactor = Math.pow(1 + (formValues.expenseGrowthRate || 0) / 100, month);
        monthlyExpenses = Math.min(monthlyExpenses * growthFactor, monthlyExpenses * (1 + (formValues.expenseGrowthCap || 0) / 100));
      }

      // Total expenses = base OPEX + team salaries + operating costs
      const totalExpenses = monthlyExpenses + teamSalaries;

      const netCashFlow = totalRevenue - totalExpenses;
      const cumulativeCash: number = data.length > 0 ? (data[data.length - 1] as any).cumulativeCash + netCashFlow : netCashFlow;

      data.push({
        month: month + 1,
        revenue: totalRevenue,
        expenses: totalExpenses, // Total expenses including salaries and operating costs
        operatingExpenses: monthlyExpenses, // Base operating expenses
        baseExpenses: formValues.monthlyOpex || 0, // Base OPEX
        teamSalaries: teamSalaries, // Team salaries
        users,
        netCashFlow,
        cumulativeCash,
        soloRevenue,
        teamRevenue,
        unlimitedRevenue,
      });
    }

    return data;
  }, [formValues, planningMonths, teamRoles]);

  // Calculate break-even month
  const breakEvenMonthValue = useMemo(() => {
    if (!projectionData.length) return null;
    return projectionData.findIndex(item => item.cumulativeCash >= 0) + 1 || null;
  }, [projectionData]);

  // Calculate final metrics
  const finalMetrics = useMemo(() => {
    if (!projectionData.length) return { users: 0, totalRevenue: 0, totalExpenses: 0, netCashFlow: 0 };
    const lastData = projectionData[projectionData.length - 1];
    return {
      users: lastData?.users || 0,
      totalRevenue: projectionData.reduce((sum, item) => sum + item.revenue, 0),
      totalExpenses: projectionData.reduce((sum, item) => sum + item.expenses, 0),
      netCashFlow: lastData?.cumulativeCash || 0,
    };
  }, [projectionData]);

  // Calculate company valuation
  const companyValuation = useMemo(() => {
    if (!formValues) return 0;
    return finalMetrics.totalRevenue * (formValues.revenueMultiple || 1);
  }, [finalMetrics.totalRevenue, formValues?.revenueMultiple]);

  // Calculate investor equity value
  const investorEquityValue = useMemo(() => {
    if (!formValues) return 0;
    return (companyValuation * (formValues.equityPercentage || 0)) / 100;
  }, [companyValuation, formValues?.equityPercentage]);

  // Calculate ROI
  const roi = useMemo(() => {
    if (!formValues || formValues.investmentAmount === 0) return 0;
    const totalReturn = investorEquityValue - formValues.investmentAmount;
    return (totalReturn / formValues.investmentAmount) * 100;
  }, [investorEquityValue, formValues?.investmentAmount]);

  // Calculate startup metrics
  const startupMetrics = useMemo(() => {
    if (!projectionData.length) return {
      mrr: 0,
      arr: 0,
      monthlyBurnRate: 0,
      arpu: 0,
      runwayMonths: 0,
      estimatedCustomers: 0,
      ltvCacRatio: 3.0,
      grossMargin: 0.7,
    };

    return {
      mrr: finalMetrics.totalRevenue / 12,
      arr: finalMetrics.totalRevenue,
      monthlyBurnRate: finalMetrics.totalExpenses / projectionData.length,
      arpu: finalMetrics.users > 0 ? finalMetrics.totalRevenue / finalMetrics.users : 0,
      runwayMonths: finalMetrics.netCashFlow > 0 ? Math.floor(finalMetrics.netCashFlow / (finalMetrics.totalExpenses / projectionData.length)) : 0,
      estimatedCustomers: finalMetrics.users,
      ltvCacRatio: 3.0, // Placeholder
      grossMargin: 0.7, // Placeholder
    };
  }, [finalMetrics, projectionData.length]);

  // Format currency helper
  const formatCurrency = (value: number) => {
    const convertedValue = value * CURRENCY_RATES[currency];
    const currencyConfig: Record<Currency, { locale: string; currency: string }> = {
      USD: { locale: "en-US", currency: "USD" },
      AED: { locale: "ar-AE", currency: "AED" },
      INR: { locale: "en-IN", currency: "INR" },
    };
    const config = currencyConfig[currency];
    return new Intl.NumberFormat(config.locale, {
      style: "currency",
      currency: config.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(convertedValue);
  };

  return {
    projectionData,
    breakEvenMonthValue,
    finalMetrics,
    companyValuation,
    investorEquityValue,
    roi,
    startupMetrics,
    formatCurrency,
  };
}

